import { z } from 'zod';
export const reportingDefinitionsCapability = 'reporting.definitions.v1';
export const reportingExecutionCapability = 'reporting.execution.v1';
export const reportingFinanceProjectPerformanceCapability = 'reporting.finance-project-performance.v1';
export const reportingTrainingTeamSummaryCapability = 'reporting.training-team-summary.v1';
export const reportingDatasetKeySchema = z.enum([
    'finance-project-performance',
    'training-team-summary',
]);
export const reportingDefinitionStatusSchema = z.enum(['draft', 'active', 'archived']);
export const reportingChartTypeSchema = z.enum(['table', 'bar']);
export const reportingFinanceProjectPerformanceColumnSchema = z.enum([
    'projectNumber', 'projectName', 'projectStatus', 'invoiceCount', 'invoicedCents',
    'collectedCents', 'writtenOffCents', 'outstandingCents', 'approvedDirectExpenseCount',
    'approvedDirectExpenseCents', 'referralCommissionCount', 'referralCommissionCents',
    'overheadCents', 'knownFinanceCostCents', 'knownFinanceContributionCents',
]);
export const reportingTrainingTeamSummaryColumnSchema = z.enum([
    'membershipDisplayName', 'assignmentTotal', 'assignmentOpen', 'assignmentCompleted',
    'assignmentWaived', 'assignmentOverdue', 'completedCourseCount', 'assessmentAttemptCount',
    'assessmentPassedCount', 'averageScoreBasisPoints', 'latestAssessmentSubmittedAt',
    'activitySessionCount', 'activityActiveSeconds', 'latestActivityAt',
]);
export const reportingColumnSchema = z.union([
    reportingFinanceProjectPerformanceColumnSchema,
    reportingTrainingTeamSummaryColumnSchema,
]);
export const reportingFilterOperatorSchema = z.enum(['equals', 'contains', 'gte', 'lte']);
function validateFilter(filter, textFields, context) {
    const textField = textFields.has(filter.field);
    if (textField !== (typeof filter.value === 'string')) {
        context.addIssue({ code: 'custom', path: ['value'], message: 'Filter value type is invalid.' });
    }
    if (filter.operator === 'contains' && !textField) {
        context.addIssue({ code: 'custom', path: ['operator'], message: 'Contains is text-only.' });
    }
    if ((filter.operator === 'gte' || filter.operator === 'lte') && textField) {
        context.addIssue({ code: 'custom', path: ['operator'], message: 'Range filters are numeric-only.' });
    }
}
const filterValueSchema = z.union([
    z.string().trim().min(1).max(160),
    z.number().int().safe(),
]);
const financeTextFields = new Set(['projectNumber', 'projectName', 'projectStatus']);
const trainingTextFields = new Set([
    'membershipDisplayName', 'latestAssessmentSubmittedAt', 'latestActivityAt',
]);
export const reportingFinanceProjectPerformanceFilterSchema = z.strictObject({
    field: reportingFinanceProjectPerformanceColumnSchema,
    operator: reportingFilterOperatorSchema,
    value: filterValueSchema,
}).superRefine((filter, context) => validateFilter(filter, financeTextFields, context));
export const reportingTrainingTeamSummaryFilterSchema = z.strictObject({
    field: reportingTrainingTeamSummaryColumnSchema,
    operator: reportingFilterOperatorSchema,
    value: filterValueSchema,
}).superRefine((filter, context) => validateFilter(filter, trainingTextFields, context));
export const reportingFilterSchema = z.union([
    reportingFinanceProjectPerformanceFilterSchema,
    reportingTrainingTeamSummaryFilterSchema,
]);
export const reportingFinanceProjectPerformanceSortSchema = z.strictObject({
    field: reportingFinanceProjectPerformanceColumnSchema,
    direction: z.enum(['asc', 'desc']),
});
export const reportingTrainingTeamSummarySortSchema = z.strictObject({
    field: reportingTrainingTeamSummaryColumnSchema,
    direction: z.enum(['asc', 'desc']),
});
export const reportingSortSchema = z.union([
    reportingFinanceProjectPerformanceSortSchema,
    reportingTrainingTeamSummarySortSchema,
]);
function uniqueColumns(definition, context) {
    if (new Set(definition.columns).size !== definition.columns.length) {
        context.addIssue({ code: 'custom', path: ['columns'], message: 'Columns cannot repeat.' });
    }
}
const reportingFinanceProjectPerformanceDefinitionValueShape = {
    datasetKey: z.literal('finance-project-performance'),
    columns: z.array(reportingFinanceProjectPerformanceColumnSchema).min(1).max(15),
    filters: z.array(reportingFinanceProjectPerformanceFilterSchema).max(10).default([]),
    sort: reportingFinanceProjectPerformanceSortSchema,
    rowLimit: z.number().int().min(1).max(500),
    chartType: reportingChartTypeSchema,
};
export const reportingFinanceProjectPerformanceDefinitionValueSchema = z
    .strictObject(reportingFinanceProjectPerformanceDefinitionValueShape)
    .superRefine(uniqueColumns);
const reportingTrainingTeamSummaryDefinitionValueShape = {
    datasetKey: z.literal('training-team-summary'),
    columns: z.array(reportingTrainingTeamSummaryColumnSchema).min(1).max(14),
    filters: z.array(reportingTrainingTeamSummaryFilterSchema).max(10).default([]),
    sort: reportingTrainingTeamSummarySortSchema,
    rowLimit: z.number().int().min(1).max(500),
    chartType: reportingChartTypeSchema,
};
export const reportingTrainingTeamSummaryDefinitionValueSchema = z
    .strictObject(reportingTrainingTeamSummaryDefinitionValueShape)
    .superRefine(uniqueColumns);
export const reportingDefinitionValueSchema = z.discriminatedUnion('datasetKey', [
    reportingFinanceProjectPerformanceDefinitionValueSchema,
    reportingTrainingTeamSummaryDefinitionValueSchema,
]);
const versionMetadata = {
    id: z.string().uuid(), tenantId: z.string().uuid(), definitionId: z.string().uuid(),
    versionNumber: z.number().int().positive(), sourceVersionId: z.string().uuid().nullable(),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/), reason: z.string().trim().min(5).max(500),
    createdByMembershipId: z.string().uuid(), createdAt: z.string().datetime({ offset: true }),
};
export const reportingDefinitionVersionSchema = z.discriminatedUnion('datasetKey', [
    z.strictObject({
        ...reportingFinanceProjectPerformanceDefinitionValueShape,
        ...versionMetadata,
    }).superRefine(uniqueColumns),
    z.strictObject({
        ...reportingTrainingTeamSummaryDefinitionValueShape,
        ...versionMetadata,
    }).superRefine(uniqueColumns),
]);
export const reportingDefinitionSchema = z.strictObject({
    id: z.string().uuid(), tenantId: z.string().uuid(), name: z.string().trim().min(1).max(160),
    description: z.string().max(1_000).nullable(), status: reportingDefinitionStatusSchema,
    activeVersionId: z.string().uuid().nullable(), latestVersionId: z.string().uuid(),
    revision: z.number().int().positive(), archivedAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: z.string().uuid(), updatedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }), updatedAt: z.string().datetime({ offset: true }),
    activeVersion: reportingDefinitionVersionSchema.nullable(),
    latestVersion: reportingDefinitionVersionSchema,
});
export const reportingDefinitionListQuerySchema = z.strictObject({
    includeArchived: z.boolean().default(false), limit: z.number().int().min(1).max(250).default(50),
    offset: z.number().int().nonnegative().default(0),
});
export const reportingCreateDefinitionSchema = z.strictObject({
    commandId: z.string().uuid(), name: z.string().trim().min(1).max(160),
    description: z.string().trim().max(1_000).nullable().default(null),
    definition: reportingDefinitionValueSchema, reason: z.string().trim().min(5).max(500),
});
export const reportingAppendDefinitionVersionSchema = z.strictObject({
    commandId: z.string().uuid(), expectedRevision: z.number().int().positive(),
    definition: reportingDefinitionValueSchema, reason: z.string().trim().min(5).max(500),
});
export const reportingPublishDefinitionSchema = z.strictObject({
    commandId: z.string().uuid(), expectedRevision: z.number().int().positive(),
    versionId: z.string().uuid(), reason: z.string().trim().min(10).max(500),
});
export const reportingArchiveDefinitionSchema = z.strictObject({
    commandId: z.string().uuid(), expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(10).max(500),
});
export const reportingRollbackDefinitionSchema = reportingArchiveDefinitionSchema.extend({
    sourceVersionId: z.string().uuid(),
});
const reportingCommandMetadataSchema = z.strictObject({
    commandId: z.string().uuid(), idempotent: z.boolean(),
});
export const reportingDefinitionResponseSchema = z.strictObject({
    data: reportingDefinitionSchema, command: reportingCommandMetadataSchema.optional(),
});
export const reportingDefinitionsResponseSchema = z.strictObject({
    data: z.array(reportingDefinitionSchema),
    page: z.strictObject({
        limit: z.number().int().positive(), offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(), total: z.number().int().nonnegative(),
    }),
});
export const reportingDefinitionVersionsResponseSchema = z.strictObject({
    data: z.array(reportingDefinitionVersionSchema),
});
//# sourceMappingURL=reporting-definition.contract.js.map